import Axios from "axios";
import S3 from "react-aws-s3";
window.Buffer = window.Buffer || require("buffer").Buffer;

const axios = Axios.create({
  baseURL: process.env.REACT_APP_S3_URL,
});

export const config = {
  bucketName: process.env.REACT_APP_BUCKET_NAME,
  dirName: process.env.REACT_APP_DIR_NAME,
  region: process.env.REACT_APP_REGION,
  accessKeyId: process.env.REACT_APP_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_SECRET_ACCESS_KEY,
};

const LEGACY_DIR_TO_BUCKET = {
  "dev-data": "swoodle-dev-panel",
  "stag-data": "swoodle-staging-panel",
  "prod-data": "swoodle-prod-panel",
};

const buildStoredObjectKey = (filename) => {
  const cleanFilename = String(filename || "").replace(/^\/+/, "");
  if (!cleanFilename) return "";

  const dirName = String(process.env.REACT_APP_DIR_NAME || "").replace(
    /^\/+|\/+$/g,
    "",
  );

  if (!dirName) return cleanFilename;
  if (cleanFilename === dirName || cleanFilename.startsWith(`${dirName}/`)) {
    return cleanFilename;
  }

  return `${dirName}/${cleanFilename}`;
};

const appendCacheBuster = (url) => {
  if (!url) return "";
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}t=${Math.floor(Date.now() / 60000)}`;
};

const normalizePathValue = (path) => {
  if (!path) return "";

  let normalizedPath = String(path).trim();
  if (!normalizedPath) return "";

  if (/^(https?:)?\/\//i.test(normalizedPath)) {
    try {
      normalizedPath = decodeURIComponent(
        new URL(normalizedPath).pathname || "",
      );
    } catch (_) {
      return normalizedPath;
    }
  }

  return normalizedPath.split(/[?#]/)[0].replace(/^\/+/, "");
};

const buildBucketUrl = (bucketName, objectKey) => {
  if (!bucketName || !objectKey) return "";
  const region = config.region;
  const cleanKey = objectKey.replace(/^\/+/, "");
  return region
    ? `https://${bucketName}.s3.${region}.amazonaws.com/${cleanKey}`
    : `https://${bucketName}.s3.amazonaws.com/${cleanKey}`;
};

const uniqueUrls = (urls) => [...new Set(urls.filter(Boolean))];

export const getS3PublicUrlCandidates = (path) => {
  if (!path) return [];

  const rawPath = String(path).trim();
  const normalizedPath = normalizePathValue(rawPath);
  if (!normalizedPath && !/^(https?:)?\/\//i.test(rawPath)) return [];

  if (/^(https?:)?\/\//i.test(rawPath)) {
    return uniqueUrls([appendCacheBuster(rawPath)]);
  }

  const s3Base = (process.env.REACT_APP_S3_URL || "").replace(/\/$/, "");
  const dirName = process.env.REACT_APP_DIR_NAME || "";
  const { bucketName, region } = config;
  const [firstSegment] = normalizedPath.split("/");

  if (LEGACY_DIR_TO_BUCKET[firstSegment]) {
    return uniqueUrls([
      appendCacheBuster(
        buildBucketUrl(LEGACY_DIR_TO_BUCKET[firstSegment], normalizedPath),
      ),
    ]);
  }

  let cleanPath = normalizedPath;
  if (
    dirName &&
    (cleanPath.startsWith(dirName + "/") || cleanPath === dirName)
  ) {
    if (s3Base.endsWith("/" + dirName) || s3Base.endsWith(dirName)) {
      cleanPath = cleanPath.substring(dirName.length);
      if (cleanPath.startsWith("/")) {
        cleanPath = cleanPath.slice(1);
      }
    }
  }

  const relativePath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  const candidates = [];

  if (s3Base) {
    candidates.push(appendCacheBuster(`${s3Base}${relativePath}`));
  }

  if (bucketName && region) {
    candidates.push(
      appendCacheBuster(
        `https://${bucketName}.s3.${region}.amazonaws.com${relativePath}`,
      ),
    );
    if (normalizedPath && !normalizedPath.includes("/")) {
      const envQualifiedKey = buildStoredObjectKey(normalizedPath);
      candidates.push(
        appendCacheBuster(buildBucketUrl(bucketName, envQualifiedKey)),
      );
      candidates.push(
        appendCacheBuster(buildBucketUrl(bucketName, normalizedPath)),
      );
    }
  }

  candidates.push(relativePath);

  return uniqueUrls(candidates);
};

export const getFileFromS3 = async (url) => {
  let file = await axios.get(url, {
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      Expires: "0",
    },
  });
  return file.data;
};

export const deleteFile = async (url) => {
  const ReactS3Client = new S3(config);
  return await ReactS3Client.deleteFile(url)
    .then((response) => {
      console.log(response);
    })
    .catch((err) => console.error(err));
};

export const getS3PublicUrl = (path) => {
  return getS3PublicUrlCandidates(path)[0] || "";
};

export const deleteAndUploadFile = async (data, filename) => {
  try {
    const ReactS3Client = new S3(config);
    // Attempt to delete existing if possible, but don't fail if it doesn't exist
    try {
      await ReactS3Client.deleteFile(filename);
    } catch (_) {}
    return await uploadFile(data, filename);
  } catch (err) {
    console.error("Delete and Upload Error:", err);
    return null;
  }
};

export const uploadFile = async (file, filename) => {
  const ReactS3Client = new S3(config);
  const storedObjectKey = buildStoredObjectKey(filename);

  try {
    const data = await ReactS3Client.uploadFile(file, filename);
    console.log("S3 Upload Success Data:", data);

    /**
     * Persist the current environment prefix with the object key so dev/staging/prod
     * never need to guess which bucket a bare filename belongs to when rebuilding URLs.
     */
    return storedObjectKey;
  } catch (err) {
    /**
     * SUCCESS RECOVERY: react-aws-s3 v1.5.0 often throws an error on 204 No Content
     * despite a successful upload. We treat this as success.
     */
    const isActuallySuccess =
      !err ||
      (typeof err === "object" && Object.keys(err).length === 0) ||
      String(err).includes("204") ||
      err?.status === 204;

    if (isActuallySuccess) {
      console.log(
        "Treating 204/Empty error as success. Returning object key:",
        storedObjectKey,
      );
      return storedObjectKey;
    }

    console.error("Real S3 Upload error:", err);
    throw err;
  }
};
